/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Image,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
  type ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { BarcodeScannerModal } from '../../shared/ui/BarcodeScannerModal';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS } from '../../shared/product-search/productSearchUx';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalShell } from '../../shared/modals/ModalShell'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { ProminentSearchWithScanner } from '../../shared/ui/ProminentSearchWithScanner';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { numericTextInputDefaults, sanitizeNumericInputText } from '../../shared/numeric/public-api';
import { formatStockQuantity, stockQuantityAllowsFraction } from '../application/inventoryUtils';
import {
  countPhysicalCountInvalidRows,
  countPhysicalCountNewStockRows,
  isIntegerUnitDecimalAttempt,
  isPhysicalCountRowInvalid,
} from '../application/physicalCountCore';
import { matchesSearchText } from '../../shared/format/searchText';
import { parseQtyInput, type UnitOfMeasure } from '@shared/quantity';

type PhysicalCountRow = {
  id: string;
  productId: string;
  stockId: string | null;
  productName: string;
  productSku: string | null;
  systemQty: number;
  countedQty: string;
  unitOfMeasure: UnitOfMeasure;
  /** ID de variante si esta fila es una celda de variante; null/undefined para parent. */
  variantId?: string | null;
  /** Label legible de la variante; null/undefined para parent. */
  variantLabel?: string | null;
  /** Thumb: imagen de variante si hay, si no la del producto. */
  imageUrl?: string | null;
};

type Props = {
  visible: boolean;
  rows: PhysicalCountRow[];
  loading: boolean;
  saving: boolean;
  onClose: () => void;
  onChangeQty: (id: string, value: string) => void;
  onApply: () => void;
  onScanCode: (rawCode: string) => Promise<string | null>;
  showBarcodeCameraButton?: boolean;
};

const SHOW_ALL_WARN_THRESHOLD = 80;

/** Thumb 36px con placeholder neutro — ayuda a confirmar el ítem al escanear. */
const CountRowThumb = ({ imageUrl }: { imageUrl?: string | null }) => {
  const [failed, setFailed] = useState(false);
  const show = Boolean(imageUrl) && !failed;
  return (
    <View style={styles.rowThumb}>
      {show ? (
        <Image
          source={{ uri: imageUrl! }}
          style={styles.rowThumbImage}
          resizeMode="contain"
          onError={() => setFailed(true)}
        />
      ) : (
        <Ionicons name="cube-outline" size={16} color={ticketColors.ink3} />
      )}
    </View>
  );
};

export const PhysicalCountModal = ({
  visible,
  rows,
  loading,
  saving,
  onClose,
  onChangeQty,
  onApply,
  onScanCode,
  showBarcodeCameraButton = true,
}: Props) => {
  const scrollRef = useRef<ScrollView>(null);
  const contentRef = useRef<View>(null);
  const { width, height } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [onlyWithStock, setOnlyWithStock] = useState(true);
  const [scannerVisible, setScannerVisible] = useState(false);
  const [integerHintIds, setIntegerHintIds] = useState<Record<string, true>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [zeroCostConfirmOpen, setZeroCostConfirmOpen] = useState(false);
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const { confirm: confirmZeroCost, ConfirmModal: ZeroCostConfirmModal } = useOperationalConfirm();
  const listMaxH = useMemo(() => Math.min(420, Math.round(height * 0.45)), [height]);

  // Reset del estado local al cerrar el modal (patrón "ajustar estado en
  // render" — evita setState dentro de un efecto).
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (!visible) {
      setFilter('');
      setShowAll(false);
      setOnlyWithStock(true);
      setScannerVisible(false);
      setIntegerHintIds({});
      setDiscardConfirmOpen(false);
      setZeroCostConfirmOpen(false);
    }
  }

  const diffCount = useMemo(
    () =>
      rows.filter((row) => {
        const counted = parseQtyInput(row.countedQty, row.unitOfMeasure);
        return counted !== null && counted !== row.systemQty;
      }).length,
    [rows]
  );

  // Filas con valor cargado que no se entiende: se descartan al aplicar.
  const invalidCount = useMemo(() => countPhysicalCountInvalidRows(rows), [rows]);
  // Filas sin stock previo que el conteo crea con costo $0.
  const newStockCount = useMemo(() => countPhysicalCountNewStockRows(rows), [rows]);
  const withStockCount = useMemo(() => rows.filter((r) => r.stockId != null).length, [rows]);
  const pendingEntries = diffCount + invalidCount;

  // Cierre guardado: si hay conteo cargado, confirmar antes de descartar
  // (mismo patrón que el dirty-guard del form de catálogo).
  const closeLocked = saving || loading;
  const guardedClose = useCallback(() => {
    if (closeLocked) return;
    // Si un confirm ya está arriba, Escape/back lo cierra a él.
    if (discardConfirmOpen || zeroCostConfirmOpen) return;
    if (pendingEntries === 0) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar el conteo',
      message:
        diffCount > 0
          ? `Perdés ${diffCount} diferencia${diffCount === 1 ? '' : 's'} cargada${diffCount === 1 ? '' : 's'}.`
          : 'Perdés los valores que cargaste.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  }, [
    closeLocked,
    discardConfirmOpen,
    zeroCostConfirmOpen,
    pendingEntries,
    diffCount,
    onClose,
    confirmDiscard,
  ]);

  const handleApply = useCallback(() => {
    if (closeLocked) return;
    if (discardConfirmOpen || zeroCostConfirmOpen) return;
    if (newStockCount === 0) {
      onApply();
      return;
    }
    // Crear stock sin fila previa implica costo $0 → confirmación explícita.
    setZeroCostConfirmOpen(true);
    void confirmZeroCost({
      title: 'Productos sin costo',
      message: `${newStockCount} producto${newStockCount === 1 ? '' : 's'} no ${
        newStockCount === 1 ? 'tiene' : 'tienen'
      } stock cargado. ${
        newStockCount === 1 ? 'Va a entrar' : 'Van a entrar'
      } con costo $0 y quedar valuado${newStockCount === 1 ? '' : 's'} en $0.`,
      confirmLabel: 'Aplicar igual',
      destructive: true,
      iconName: 'alert-circle-outline',
    }).then((confirmed) => {
      setZeroCostConfirmOpen(false);
      if (confirmed) onApply();
    });
  }, [closeLocked, discardConfirmOpen, zeroCostConfirmOpen, newStockCount, confirmZeroCost, onApply]);

  // Intercepta el texto crudo: en unidades enteras el separador decimal se
  // sanitiza en silencio (12,5 → 125). Marcamos la fila con un hint.
  const handleCountedQtyChange = useCallback(
    (item: PhysicalCountRow, raw: string) => {
      const allowsFraction = stockQuantityAllowsFraction(item.unitOfMeasure);
      if (isIntegerUnitDecimalAttempt(raw, item.unitOfMeasure)) {
        setIntegerHintIds((prev) => (prev[item.id] ? prev : { ...prev, [item.id]: true }));
      }
      onChangeQty(item.id, sanitizeNumericInputText(raw, allowsFraction ? 'decimalMoney' : 'integer'));
    },
    [onChangeQty]
  );

  const displayRows = useMemo(() => {
    const q = filter.trim();
    const hasQuery = q.length > 0;
    const queryReady = q.length >= MIN_PRODUCT_SEARCH_QUERY_CHARS;

    if (!showAll && !queryReady) {
      return [];
    }

    let list = rows;
    if (hasQuery) {
      list = rows.filter(
        (r) =>
          matchesSearchText(r.productName, q) ||
          matchesSearchText(r.productSku, q) ||
          matchesSearchText(r.variantLabel, q)
      );
    } else if (onlyWithStock) {
      // "Ver todo" muestra por defecto solo las filas con stock cargado —
      // el toggle abajo permite ver el catálogo completo.
      list = list.filter((r) => r.stockId != null);
    }

    return [...list].sort((a, b) => {
      const byName = a.productName.localeCompare(b.productName, 'es', { sensitivity: 'base' });
      if (byName !== 0) return byName;
      // Dentro de un mismo producto: parent (sin variante) primero, luego variantes por label.
      const aIsVariant = a.variantId != null;
      const bIsVariant = b.variantId != null;
      if (aIsVariant !== bIsVariant) return aIsVariant ? 1 : -1;
      return (a.variantLabel ?? '').localeCompare(b.variantLabel ?? '', 'es', { sensitivity: 'base' });
    });
  }, [rows, filter, showAll, onlyWithStock]);

  const listVisible = showAll || filter.trim().length >= MIN_PRODUCT_SEARCH_QUERY_CHARS;
  const filterHint =
    filter.trim().length > 0 && filter.trim().length < MIN_PRODUCT_SEARCH_QUERY_CHARS
      ? `Escribí al menos ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres para filtrar.`
      : null;

  const handlePhysicalCountScan = async (rawCode: string) => {
    setScannerVisible(false);
    try {
      const nextFilter = await onScanCode(rawCode);
      if (nextFilter != null && nextFilter.trim() !== '') {
        setFilter(nextFilter.trim());
      } else {
        setFilter(rawCode.trim());
      }
      setShowAll(false);
    } catch {
      setFilter(rawCode.trim());
      setShowAll(false);
    }
  };

  // Alcance honesto: el conteo cubre el catálogo completo (incluye los
  // productos que todavía no tienen stock en esta sucursal).
  const heroSubtitle = loading
    ? 'Cargando productos y stock de la sucursal…'
    : rows.length === 0
      ? 'No hay productos físicos en el catálogo para esta sucursal.'
      : `${rows.length} producto${rows.length === 1 ? '' : 's'} del catálogo · ${withStockCount} con stock cargado. Buscá o escaneá para contar. Al terminar, aplicá las diferencias.`;

  const footerActionsStyle = isMobile ? styles.footerActionsStack : styles.footerActions;
  const btnSecondaryStyle = isMobile ? [styles.mobileBtnSecondary] : [posUi.secondaryButton, styles.btnSecondary];
  const btnPrimaryStyle = isMobile ? [styles.mobileBtnPrimary] : [posUi.primaryButton, styles.btnPrimary];
  const btnSecondaryTextStyle = isMobile ? styles.mobileBtnSecondaryText : posUi.secondaryButtonText;
  const btnPrimaryTextStyle = isMobile ? styles.mobileBtnPrimaryText : posUi.primaryButtonText;

  return (
    <>
      <ModalShell
        visible={visible}
        onClose={guardedClose}
        appearance="ticket"
        kicker="Stock"
        title="Conteo físico"
        heroKicker="Conteo"
        heroSubtitle={heroSubtitle}
        heroCount={diffCount > 0 ? diffCount : rows.length}
        heroCountLabel={diffCount > 0 ? 'con diferencia' : 'líneas'}
        widthVariant="comfortable"
        scrollRef={scrollRef}
        contentRef={contentRef}
        scrollBottomInset={spacing.sm}
        scrollPadding={spacing.md}
        scrollContentStyle={{ gap: spacing.sm }}
        footer={
          loading ? null : (
            <View style={footerActionsStyle}>
              <TouchableOpacity style={btnSecondaryStyle} onPress={guardedClose} accessibilityRole="button" accessibilityLabel="Cancelar conteo físico">
                <Text style={btnSecondaryTextStyle}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[btnPrimaryStyle, saving && posUi.disabledCard]}
                onPress={handleApply}
                disabled={saving}
                accessibilityRole="button"
                accessibilityLabel={saving ? 'Aplicando conteo físico' : 'Aplicar conteo físico'}
              >
                <Text style={btnPrimaryTextStyle}>{saving ? 'Aplicando…' : 'Aplicar'}</Text>
              </TouchableOpacity>
            </View>
          )
        }
      >
        {!loading ? (
          <>
            <ProminentSearchWithScanner
              sectionLabel={null}
              unifiedField
              scanTone="neutral"
              value={filter}
              onChangeText={(text) => {
                setFilter(text);
                if (text.trim().length === 0) {
                  setShowAll(false);
                }
              }}
              onPressScan={() => setScannerVisible(true)}
              showBarcodeCameraButton={showBarcodeCameraButton}
              placeholder={`Nombre o SKU (min. ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres)`}
              placeholderCompact={`Filtrar (${MIN_PRODUCT_SEARCH_QUERY_CHARS}+)`}
              accessibilityLabel="Buscar en conteo físico"
            />
            {filterHint ? <Text style={styles.filterHint}>{filterHint}</Text> : null}
            {!listVisible && rows.length > 0 ? (
              <View style={styles.preSearchBlock}>
                <ModalStatePanel
                  empty
                  emptyTitle="Buscar o escanear"
                  emptyMessage="Buscá o escaneá un producto. Si querés ver todos, usá los enlaces de abajo."
                />
                <TouchableOpacity
                  style={styles.showAllLink}
                  onPress={() => {
                    setOnlyWithStock(true);
                    setShowAll(true);
                  }}
                  accessibilityRole="button"
                >
                  <Text style={modalExperience.accentLink}>
                    Ver solo con stock ({withStockCount} línea{withStockCount === 1 ? '' : 's'})
                  </Text>
                </TouchableOpacity>
                {rows.length > withStockCount ? (
                  <TouchableOpacity
                    style={styles.showAllLink}
                    onPress={() => {
                      setOnlyWithStock(false);
                      setShowAll(true);
                    }}
                    accessibilityRole="button"
                  >
                    <Text style={modalExperience.accentLink}>
                      Ver todo el catálogo ({rows.length} línea{rows.length === 1 ? '' : 's'})
                      {rows.length > SHOW_ALL_WARN_THRESHOLD ? ' — son muchos' : ''}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
            {listVisible && showAll ? (
              <View style={styles.listMetaRow}>
                {onlyWithStock && rows.length > withStockCount ? (
                  <TouchableOpacity onPress={() => setOnlyWithStock(false)} hitSlop={8} accessibilityRole="button">
                    <Text style={modalExperience.accentLink}>Ver catálogo completo ({rows.length})</Text>
                  </TouchableOpacity>
                ) : null}
                {!onlyWithStock && rows.length > withStockCount ? (
                  <TouchableOpacity onPress={() => setOnlyWithStock(true)} hitSlop={8} accessibilityRole="button">
                    <Text style={modalExperience.accentLink}>Solo con stock ({withStockCount})</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity onPress={() => setShowAll(false)} hitSlop={8} accessibilityRole="button">
                  <Text style={modalExperience.accentLink}>Solo búsqueda</Text>
                </TouchableOpacity>
              </View>
            ) : null}
            {newStockCount > 0 ? (
              <Text style={styles.zeroCostHint}>
                {newStockCount} producto{newStockCount === 1 ? '' : 's'} sin stock previo: entra
                {newStockCount === 1 ? '' : 'n'} con costo $0.
              </Text>
            ) : null}
          </>
        ) : null}
        {loading ? (
          <ModalStatePanel loading loadingMessage="Cargando productos…" />
        ) : listVisible ? (
          <View style={[modalExperience.listContainer, { maxHeight: listMaxH }]}>
            <FlatList
              data={displayRows}
              keyExtractor={(item) => item.id}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              initialNumToRender={16}
              windowSize={8}
              ListEmptyComponent={
                <Text style={[posUi.checkoutHint, styles.empty]}>
                  {rows.length === 0
                    ? 'No hay líneas de stock en esta sucursal.'
                    : showAll && onlyWithStock && filter.trim() === ''
                      ? 'No hay productos con stock cargado.'
                      : 'Ningún producto coincide con la búsqueda.'}
                </Text>
              }
              renderItem={({ item }) => {
                const counted = parseQtyInput(item.countedQty, item.unitOfMeasure);
                const diff = counted !== null ? counted - item.systemQty : 0;
                const hasDiff = diff !== 0 && counted !== null;
                const allowsFraction = stockQuantityAllowsFraction(item.unitOfMeasure);
                const isVariant = item.variantId != null;
                const rowInvalid = isPhysicalCountRowInvalid(item);
                const integerHint = !!integerHintIds[item.id];
                return (
                  <View>
                    <View style={[styles.historyRow, hasDiff && styles.historyRowDiff, isVariant && styles.historyRowVariant]}>
                      <CountRowThumb imageUrl={item.imageUrl} />
                      <View style={[styles.rowText, isVariant && styles.rowTextVariant]}>
                        <Text style={[typography.body, styles.productOptionName]} numberOfLines={2}>
                          {isVariant ? item.variantLabel ?? 'Variante' : item.productName}
                        </Text>
                        <Text style={posUi.checkoutHint}>
                          {isVariant ? 'Variante' : 'Cargado'}: {formatStockQuantity(item.systemQty, item.unitOfMeasure)}
                          {!isVariant && item.productSku ? ` · ${item.productSku}` : ''}
                          {item.stockId == null ? ' · Sin stock previo' : ''}
                        </Text>
                      </View>
                      <TextInput
                        {...numericTextInputDefaults(allowsFraction ? 'decimalMoney' : 'integer')}
                        style={[modalExperience.formInput, styles.qtyInput, rowInvalid && styles.qtyInputInvalid]}
                        value={item.countedQty}
                        onChangeText={(raw) => handleCountedQtyChange(item, raw)}
                        placeholder="Contado"
                        placeholderTextColor={ticketColors.ink3}
                      />
                      {hasDiff ? (
                        <Text
                          style={[posUi.checkoutHint, styles.diff, { color: diff > 0 ? ticketColors.ok : ticketColors.bad }]}
                        >
                          {diff > 0 ? '+' : ''}
                          {diff}
                        </Text>
                      ) : (
                        <View style={styles.diffPlaceholder} />
                      )}
                    </View>
                    {rowInvalid ? (
                      <Text style={[styles.rowHint, styles.rowHintBad]}>Este valor no se entiende: no se aplica.</Text>
                    ) : integerHint ? (
                      <Text style={[styles.rowHint, styles.rowHintWarn]}>Este producto se cuenta en unidades enteras.</Text>
                    ) : null}
                  </View>
                );
              }}
            />
          </View>
        ) : null}
      </ModalShell>
      {showBarcodeCameraButton ? (
        <BarcodeScannerModal
          visible={scannerVisible}
          title="Escanear en conteo físico"
          subtitle="Leé el código para ubicar el producto y cargar la cantidad contada."
          onClose={() => setScannerVisible(false)}
          onCodeScanned={handlePhysicalCountScan}
          requireConfirmation
        />
      ) : null}
      {DiscardConfirmModal}
      {ZeroCostConfirmModal}
    </>
  );
};

const styles = StyleSheet.create({
  rowThumb: {
    width: 36,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginRight: spacing.sm,
    flexShrink: 0,
  },
  rowThumbImage: {
    width: '100%',
    height: '100%',
  },
  filterHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  preSearchBlock: {
    gap: spacing.sm,
  },
  showAllLink: {
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  listMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  list: {
    flex: 1,
  },
  historyRow: {
    paddingHorizontal: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    paddingVertical: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  historyRowDiff: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  historyRowVariant: {
    paddingLeft: spacing.xl,
    paddingVertical: spacing.xs,
  },
  rowTextVariant: {
    flex: 1,
    minWidth: 0,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  productOptionName: {
    color: ticketColors.ink,
    fontWeight: '600',
  },
  qtyInput: {
    width: 72,
    textAlign: 'center',
  },
  qtyInputInvalid: {
    borderColor: ticketColors.bad,
  },
  rowHint: {
    ...typography.caption,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
    marginTop: -spacing.xs,
  },
  rowHintBad: {
    color: ticketColors.bad,
  },
  rowHintWarn: {
    color: ticketColors.warn,
  },
  zeroCostHint: {
    ...typography.caption,
    color: ticketColors.warn,
    paddingHorizontal: spacing.md,
  },
  diff: {
    width: 44,
    textAlign: 'right',
    fontWeight: '700',
  },
  diffPlaceholder: {
    width: 44,
  },
  empty: {
    paddingVertical: spacing.lg,
    textAlign: 'center',
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    width: '100%',
  },
  footerActionsStack: {
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },
  btnSecondary: {
    flex: 1,
    paddingHorizontal: spacing.md,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimary: {
    flex: 1,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileBtnSecondary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mobileBtnPrimary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnPrimaryText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
});
